import React, { useState, useEffect } from 'react';
import { Shield, Flag, MessageSquare, ShieldAlert, Trash2, TrendingUp, Activity, Clock } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import FlaggedListings from './components/FlaggedListings';
import DisputeQueue from './components/DisputeQueue';
import FraudDetection from './components/FraudDetection';
import TakedownActions from './components/TakedownActions';
import { supabase } from '../../lib/supabase';

const tabs = [
  { id: 'flagged', label: 'Flagged Listings', icon: Flag },
  { id: 'disputes', label: 'Dispute Queue', icon: MessageSquare },
  { id: 'fraud', label: 'Fraud Detection', icon: ShieldAlert },
  { id: 'takedowns', label: 'Takedown Actions', icon: Trash2 },
];

export default function AdminModerationDashboard() {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('flagged');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [overviewStats, setOverviewStats] = useState([
    { label: 'Active Listings', value: '—', change: 'Loading...', icon: Flag, color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/20' },
    { label: 'Open Disputes', value: '—', change: 'Loading...', icon: MessageSquare, color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20' },
    { label: 'Fraud Alerts', value: '—', change: 'Loading...', icon: ShieldAlert, color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/20' },
    { label: 'Pending Takedowns', value: '—', change: 'Loading...', icon: Trash2, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20' },
  ]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    loadModerationStats();
  }, []);

  const loadModerationStats = async () => {
    try {
      // Fetch marketplace listings with flag-related data
      const [listingsRes, disputesRes, activityRes] = await Promise.all([
        supabase?.from('marketplace_listings')?.select('id, status, created_at')?.eq('status', 'active'),
        supabase?.from('escrow_transactions')?.select('id, status, created_at')?.in('status', ['disputed', 'pending']),
        supabase?.from('user_activities')?.select('id, activity_type, title, created_at')?.order('created_at', { ascending: false })?.limit(10),
      ]);

      const listings = listingsRes?.data || [];
      const disputes = disputesRes?.data || [];
      const activities = activityRes?.data || [];

      const openDisputes = disputes?.filter(d => d?.status === 'disputed')?.length;
      const pendingEscrow = disputes?.filter(d => d?.status === 'pending')?.length;

      setOverviewStats([
        { label: 'Active Listings', value: String(listings?.length), change: 'Under monitoring', icon: Flag, color: 'text-red-400', bg: 'bg-red-400/10 border-red-400/20' },
        { label: 'Open Disputes', value: String(openDisputes), change: openDisputes > 0 ? `${openDisputes} require attention` : 'All clear', icon: MessageSquare, color: 'text-orange-400', bg: 'bg-orange-400/10 border-orange-400/20' },
        { label: 'Pending Escrow', value: String(pendingEscrow), change: 'Awaiting confirmation', icon: ShieldAlert, color: 'text-yellow-400', bg: 'bg-yellow-400/10 border-yellow-400/20' },
        { label: 'Total Transactions', value: String(disputes?.length), change: 'Escrow transactions', icon: Trash2, color: 'text-blue-400', bg: 'bg-blue-400/10 border-blue-400/20' },
      ]);

      // Map recent activities
      const mapped = activities?.slice(0, 4)?.map(a => ({
        time: getRelativeTime(a?.created_at),
        action: a?.title || a?.activity_type,
        detail: a?.title || '',
        color: a?.activity_type === 'sold' ? 'text-green-400' : a?.activity_type === 'added' ? 'text-blue-400' : 'text-yellow-400',
        dot: a?.activity_type === 'sold' ? 'bg-green-400' : a?.activity_type === 'added' ? 'bg-blue-400' : 'bg-yellow-400',
      }));
      setRecentActivity(mapped);
    } catch (err) {
      console.error('Error loading moderation stats:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const getRelativeTime = (dateStr) => {
    const diff = Date.now() - new Date(dateStr)?.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} hr ago`;
    return `${Math.floor(hrs / 24)} days ago`;
  };

  // Auth guards
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3 text-muted-foreground">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (userProfile && userProfile?.role !== 'admin') {
    return <Navigate to="/homepage" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="flex pt-16">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'} min-h-screen`}>
          <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-5 sm:mb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Shield size={18} className="text-destructive" />
                </div>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-foreground">Moderation Dashboard</h1>
                  <p className="text-xs sm:text-sm text-muted-foreground">Platform safety, fraud prevention, and dispute resolution</p>
                </div>
              </div>
            </div>

            {/* Overview Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {overviewStats?.map(stat => (
                <div key={stat?.label} className={`bg-card border rounded-xl p-3 sm:p-4 ${stat?.bg}`}>
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <span className="text-xs text-muted-foreground leading-tight">{stat?.label}</span>
                    <stat.icon size={14} className={stat?.color} />
                  </div>
                  <div className={`text-2xl sm:text-3xl font-bold ${stat?.color} mb-1`}>
                    {loadingStats ? <span className="animate-pulse">—</span> : stat?.value}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Activity size={10} />
                    <span className="truncate">{stat?.change}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Activity Pulse */}
            <div className="bg-card border border-border rounded-xl p-4 mb-5 sm:mb-6">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Recent Platform Activity</span>
                <div className="flex items-center gap-1 ml-auto">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
                  <span className="text-xs text-success">Live</span>
                </div>
              </div>
              <div className="space-y-2">
                {recentActivity?.length > 0 ? recentActivity?.map((event, i) => (
                  <div key={i} className="flex items-center gap-2 sm:gap-3 text-xs">
                    <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${event?.dot}`} />
                    <span className="text-muted-foreground w-16 sm:w-20 flex-shrink-0 flex items-center gap-1">
                      <Clock size={10} /> {event?.time}
                    </span>
                    <span className={`font-medium flex-shrink-0 ${event?.color}`}>{event?.action}</span>
                    <span className="text-muted-foreground truncate hidden sm:block">{event?.detail}</span>
                  </div>
                )) : (
                  <p className="text-xs text-muted-foreground text-center py-2">No recent activity</p>
                )}
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-border mb-5 sm:mb-6">
              <div className="flex gap-1 overflow-x-auto scrollbar-hide pb-px">
                {tabs?.map(tab => {
                  const TabIcon = tab?.icon;
                  return (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-3 sm:py-3.5 text-xs sm:text-sm font-medium border-b-2 transition-all whitespace-nowrap relative min-h-[48px] ${
                        activeTab === tab?.id
                          ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <TabIcon size={14} />
                      <span className="hidden xs:inline sm:inline">{tab?.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === 'flagged' && <FlaggedListings />}
              {activeTab === 'disputes' && <DisputeQueue />}
              {activeTab === 'fraud' && <FraudDetection />}
              {activeTab === 'takedowns' && <TakedownActions />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
